import { MutationFetcher } from 'swr/mutation';

import { apiUrl } from 'SRC/config';

import { del, get, post, put } from '../fetch';
import { getHeaders } from '../headers';

export interface RecnclnBatchList {
  results: RecnclnBatch[];
}

export const batchReprocessingStatus = {
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  REPROCESSING: 'REPROCESSING',
  UPDATED: 'UPDATED',
  FAILED: 'FAILED',
} as const;

export type BatchReprocessingStatus =
  (typeof batchReprocessingStatus)[keyof typeof batchReprocessingStatus];

export type RecnclnBatchStatus = 'updating' | 'succeeded' | 'conflicts';

export interface BatchListQuery {
  payout_date_start: string;
  payout_date_end: string;
}

export interface RecnclnBatch {
  id: string;
  batchId: string;
  amount: string;
  processorName: string;
  conflicts: number;
  merchantId: string;
  paymentMethods: string[];
  payoutCurrency: string;
  payoutDate: string;
  payoutDatetime: string;
  updatedAt: string | null;
  totalCount: number;
  reprocessingStatus: BatchReprocessingStatus;
  reprocessingCreatedAt: string | null;
  status: RecnclnBatchStatus;
}

export interface RecnclnBatchResponse {
  results: RecnclnBatch[];
  next: string | null;
  count: number;
  previous: string | null;
}

export interface RecnclnTransactionListResponse {
  results: RecnclnTransaction[];
  next: string | null;
  count: number;
  previous: string | null;
}

export interface RecnclnTransaction {
  id: null | string;
  batchTransactionId: string;
  amount: string | number | null;
  createdDate: Date | null;
  conflictReason: string | null;
  capturedDate: Date | null;
  merchantId: string | null;
  processorTransactionId: string;
  status: string | null;
  paymentMethod: string | null;
  reconciliationAmount: string | number;
  reconciliationOrderId: string;
  reconciliationResult: boolean;
  reconciliationResultHistory: ReconciliationResultHistory[];
  payoutGrossAmount: string | number;
  payoutNetAmount: string | number;
  payoutDate: Date | null;
  processorFeeAmount: string | number;
  interchangeFeeAmount: string | number;
  schemeFeeAmount: string | number;
  currencyCode: string;
  direction: string;
  orderId: string;
  payoutBatchId: string;
  payoutCurrencyCode: string;
  payoutTotalDeductionsAmount: string | number;
  processor: string;
  reconciliationCurrencyCode: string;
  transactionType: ReconciliationTransactionType;
  transactionTypeDetail: string;
}

export const ReconciliationTransactionType = {
  SALE: 'SALE',
  REFUND: 'REFUND',
  TRANSFER: 'TRANSFER',
  DISPUTE: 'DISPUTE',
  PAYOUT: 'PAYOUT',
} as const;
export type ReconciliationTransactionType =
  (typeof ReconciliationTransactionType)[keyof typeof ReconciliationTransactionType];

export type ReconciliationResultHistory = {
  actual: null;
  decisionAt: Date;
  decisionBy: string;
  expected: string;
  mismatchType: string;
};

export interface RecnclnProcessors {
  id: string;
  name: string;
  connectionId: string;
}

export interface RecnclnProcessorConfig {
  id: string;
  name: string;
  merchantIds: string[];
  connectionId: string;
  credentialsSchema: {
    type: 'API' | 'SFTP';
    fields: CredentialsSchema[];
    instructions: string;
  };
}

export interface FetchRecncConnectionsData {
  count: number;
  next: string | null;
  previous: string | null;
  results: RecnclnExistingConfigs[];
}

export interface RecnclnExistingConfigs {
  id: string;
  name: string;
  merchantId: string;
  connectionId: string;
  created: string;
  modified: string;
}

export interface CredentialsSchema {
  key: string;
  name: string;
  type: 'string' | 'secret';
}

export interface RecnclnBatchListFilter {
  format: string;
  key: string;
  label: string;
  choices?: {
    value: string;
    label: string;
  }[];
}

export const FETCH_RECNCLN_TRANSACTIONS_URL =
  '/reconciliation/v1/payouts/transactions';
export const FETCH_RECNCLN_BATCHES_URL = '/reconciliation/v1/payouts/batches';

export const FETCH_RECNCLN_PROCESSORS_URL = '/reconciliation/v1/processors';

export const FETCH_RECNCLN_PROCESSOR_CONFIG_URL =
  '/reconciliation/v1/processors-configs';

export const FETCH_RECNCLN_FILTERS_BATCH_LIST_URL =
  '/reconciliation/v1/filters/batch-list';

interface ReconciliationMerchantAccount {
  reconciliationAccess: boolean;
}

export const FETCH_RECNCLN_MERCHANT_ACCOUNT =
  '/reconciliation/v1/merchant-accounts/me';

export function fetchMerchantAccount() {
  return get<ReconciliationMerchantAccount>(
    apiUrl + FETCH_RECNCLN_MERCHANT_ACCOUNT,
    {},
    { auth: true },
  );
}

export const fetchRecncConnections = (cursor: string = '') => {
  return get<FetchRecncConnectionsData>(
    `${apiUrl}${cursor}`,
    {},
    { auth: true },
  );
};

export const fetchBatchList = (recnclnReportsFilterParamsString: string) => {
  const searchParams = new URLSearchParams();

  if (recnclnReportsFilterParamsString) {
    const filterParams = new URLSearchParams(recnclnReportsFilterParamsString);

    filterParams.forEach((value, key) => {
      searchParams.append(key, value);
    });
  }

  const searchParamsString = searchParams.toString();

  return get<RecnclnBatchResponse>(
    `${apiUrl}${FETCH_RECNCLN_BATCHES_URL}?${searchParamsString}`,
    {},
    { auth: true },
  );
};

export const fetchReconciliationTransactionList = (searchParams: string) => {
  return get<RecnclnTransactionListResponse>(
    `${apiUrl}${FETCH_RECNCLN_TRANSACTIONS_URL}?${searchParams}`,
    {},
    { auth: true },
  );
};
export const fetchBatchDetails = (id: string) => {
  return get<RecnclnBatch>(
    apiUrl + `${FETCH_RECNCLN_BATCHES_URL}/${id}`,
    {},
    { auth: true },
  );
};

export const fetchBatchListFilters = () => {
  return get<RecnclnBatchListFilter[]>(
    apiUrl + FETCH_RECNCLN_FILTERS_BATCH_LIST_URL,
    {},
    { auth: true },
  );
};

export const fetchTransactionCSV = (batchId: string) => {
  // Construct the URL with query parameters
  const queryParams = new URLSearchParams({
    batch_id: batchId,
  }).toString();

  const headers = getHeaders({ auth: true });
  headers.set('Accept', 'text/csv');
  headers.set('Content-Type', 'text/csv');

  return fetch(apiUrl + `${FETCH_RECNCLN_TRANSACTIONS_URL}?${queryParams}`, {
    headers,
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.text();

    return {
      data,
      headers: response.headers,
    };
  });
};

export const requestBatchReprocess: MutationFetcher<unknown, string, string> = (
  _,
  { arg },
) => {
  return put(
    apiUrl + `${FETCH_RECNCLN_BATCHES_URL}/${arg}/reprocess`,
    {},
    { auth: true },
  );
};

export const reprocessDismiss: MutationFetcher<unknown, string, string> = (
  _,
  { arg },
) => {
  return put(
    apiUrl + `${FETCH_RECNCLN_BATCHES_URL}/${arg}/acknowledge_reprocessing`,
    {},
    { auth: true },
  );
};

export const fetchProcessors = () => {
  return get<RecnclnProcessors[]>(
    apiUrl + FETCH_RECNCLN_PROCESSORS_URL,
    {},
    { auth: true },
  );
};

export const fetchProcessorConfig = (id: string) => {
  return get<RecnclnProcessorConfig>(
    apiUrl + `${FETCH_RECNCLN_PROCESSORS_URL}/${id}`,
    {},
    { auth: true },
  );
};

export const fetchExistingConfigs = () => {
  return get<RecnclnExistingConfigs[]>(
    apiUrl + FETCH_RECNCLN_PROCESSOR_CONFIG_URL,
    {},
    { auth: true },
  );
};

export type ProcessorConfig = {
  connectionId: string;
  merchantIds: string[];
  credentials: Record<string, string>;
};
export const setProcessorConfig: MutationFetcher<
  unknown,
  string,
  ProcessorConfig
> = (_, { arg }) => {
  return post(
    apiUrl + FETCH_RECNCLN_PROCESSOR_CONFIG_URL,
    { body: JSON.stringify(arg) },
    { auth: true },
  );
};

export const deleteProcessorConfig: MutationFetcher<unknown, string, string> = (
  _,
  { arg },
) => {
  return del(
    apiUrl + `${FETCH_RECNCLN_PROCESSOR_CONFIG_URL}/${arg}`,
    {},
    { auth: true },
  );
};
